import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Tilt from "react-parallax-tilt";
import { ProductImage } from "../ProductImage";
import { categoryPath } from "../../lib/category-slugs";

const GAMMES = [
  {
    num: "01",
    label: "Frites surgelées",
    slug: "frites",
    signature_image: "/api/static/products/ulas-extra-crunch-frites-9-9-mm.png",
    signature_name: "Factory Extra Crunch",
  },
  {
    num: "02",
    label: "Viandes",
    slug: "viandes",
    signature_image: "/api/static/products/ulas-steak-haches-100-pur-boeuf.png",
    signature_name: "ULAS Steak Haché 100 % Pur Bœuf",
  },
  {
    num: "03",
    label: "Volailles",
    slug: "volailles",
    signature_image: "/api/static/products/factory-chicken-nuggets.png",
    signature_name: "Factory Chicken Nuggets",
  },
  {
    num: "04",
    label: "Tortillas & Pains",
    slug: "tortillas-et-pains",
    signature_image: "/api/static/products/antalya-18-durum-30-cm.png",
    signature_name: "Antalya Durum 30 cm",
  },
  {
    num: "05",
    label: "Sauces",
    slug: "sauce",
    signature_image: "/api/static/products/factory-andalouse.png",
    signature_name: "Factory Andalouse",
  },
  {
    num: "06",
    label: "Conserves",
    slug: "conserve",
    signature_image: "/api/static/products/campana-thon-en-morceaux-a-l-huile.png",
    signature_name: "Campana Thon",
  },
  {
    num: "07",
    label: "Huile",
    slug: "huile",
    signature_image: "/api/static/products/factory-huile-de-tournesol.png",
    signature_name: "Factory Huile de Tournesol",
  },
  {
    num: "08",
    label: "Produits laitiers",
    slug: "produits-laitiers",
    signature_image: "/api/static/products/maestrella-mozzarella-100.png",
    signature_name: "Maestrella Mozzarella 100%",
  },
  {
    num: "09",
    label: "Desserts",
    slug: "dessert",
    signature_image: "/api/static/products/daim-chocolate-cake-with-crunchy-caramel.png",
    signature_name: "Daim Chocolate Cake",
  },
  {
    num: "10",
    label: "Spécialités",
    slug: "specialites",
    signature_image: "/api/static/products/lamb-weston-seasoned-wedges-skin-on.png",
    signature_name: "Lamb Weston Seasoned Wedges",
  },
  {
    num: "11",
    label: "Appetizers",
    slug: "appetizers",
    signature_image: "/api/static/products/lamb-weston-mozzarella-sticks.png",
    signature_name: "Lamb Weston Mozzarella Sticks",
  },
  {
    num: "12",
    label: "Emballages",
    slug: "emballages",
    signature_image: "/api/static/products/etui-a-frites-kraft.png",
    signature_name: "Étui à Frites Kraft",
  },
  { num: "13", label: "Détergents", slug: null, soon: true },
];

/**
 * useInteractivePointer — true only on devices with a fine pointer (mouse/trackpad)
 * AND when the user has not requested reduced motion. Tilt/glare are bound to this signal.
 */
const useInteractivePointer = () => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const fine = window.matchMedia("(pointer: fine)");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setEnabled(fine.matches && !reduce.matches);
    update();
    fine.addEventListener("change", update);
    reduce.addEventListener("change", update);
    return () => {
      fine.removeEventListener("change", update);
      reduce.removeEventListener("change", update);
    };
  }, []);
  return enabled;
};

/**
 * GrilleGammes13 — 13 gammes grid with hover glow + desktop tilt/glare.
 */
export const GrilleGammes13 = () => {
  const tiltActive = useInteractivePointer();

  return (
  <section
    id="gammes"
    data-testid="grille-gammes-13"
    className="relative bg-fd-bg border-t border-fd-border py-24 lg:py-32 px-6 md:px-10 lg:px-14 scroll-mt-24 lg:scroll-mt-28"
  >
    <div className="max-w-[1400px] mx-auto">
      <div className="mb-14 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <div className="mono-label-gold mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-fd-gold"></span>
            <span>La gamme / 13 catégories</span>
          </div>
          <h2 className="display-xl text-3xl sm:text-5xl lg:text-7xl text-fd-text max-w-3xl">
            Une seule facture. <br />
            <span className="text-gradient-gold">Treize gammes.</span>
          </h2>
        </div>
        <p className="font-body text-fd-text/50 max-w-sm">
          Surgelé, frais, sec, propre — on couvre tout. Tu commandes ce qu'il te faut. On livre. Tu ouvres.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-px bg-fd-border">
        {GAMMES.map((g, i) => {
          if (g.soon) {
            return (
              <div
                key={g.num}
                className="relative bg-fd-bg min-h-[180px] sm:aspect-square p-4 sm:p-5 lg:p-6 flex flex-col justify-between opacity-40"
                data-testid={`gamme-card-${g.num}`}
              >
                <span className="font-mono text-[11px] tracking-[0.14em] text-fd-text/50">{g.num}</span>
                <div>
                  <h3 className="font-display text-base sm:text-lg lg:text-2xl uppercase tracking-tight leading-tight">
                    {g.label}
                  </h3>
                  <span className="mono-label-gold mt-2 block">Bientôt</span>
                </div>
              </div>
            );
          }
          return (
            <motion.div
              key={g.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: Math.min(i * 0.03, 0.35) }}
              data-testid={`gamme-card-${g.num}`}
            >
              <Tilt
                tiltEnable={tiltActive}
                glareEnable={tiltActive}
                tiltMaxAngleX={6}
                tiltMaxAngleY={6}
                perspective={1100}
                transitionSpeed={1400}
                glareMaxOpacity={0.18}
                glareColor="#D4A24C"
                glareBorderRadius="0px"
                scale={1}
              >
              <Link
                to={categoryPath(g.slug)}
                data-cursor-hover
                className="group relative bg-fd-bg min-h-[240px] sm:aspect-square p-4 sm:p-5 lg:p-6 flex flex-col justify-between overflow-hidden transition-all duration-500 hover:bg-fd-elevated"
              >
                {/* Subtle spotlight at rest — warm vitrine feel */}
                <div
                  className="absolute inset-0 bg-[radial-gradient(circle_at_50%_55%,rgba(212,162,76,0.07),transparent_60%)] pointer-events-none opacity-70 group-hover:opacity-100 transition-opacity duration-500"
                  aria-hidden="true"
                ></div>
                {/* Gold glow on hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_center,rgba(212,162,76,0.12),transparent_70%)]"></div>

                <div className="relative flex items-center justify-between">
                  <span className="font-mono text-[11px] tracking-[0.14em] text-fd-text/50 group-hover:text-fd-gold transition-colors duration-500">
                    {g.num}
                  </span>
                  <ArrowUpRight
                    size={16}
                    className="text-fd-text/30 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-500"
                  />
                </div>

                {/* Signature product in a cream packshot frame — uniform with Hero/Packaging pattern. */}
                {g.signature_image && (
                  <div className="relative mx-auto my-2 fd-stage rounded-2xl p-3 sm:p-3.5 lg:p-4 drop-shadow-[0_18px_32px_rgba(0,0,0,0.5)] transition-all duration-500 group-hover:scale-[1.04]">
                    <ProductImage
                      path={g.signature_image}
                      alt={g.signature_name || g.label}
                      loading="lazy"
                      decoding="async"
                      className="block h-24 sm:h-28 lg:h-32 xl:h-36 w-auto max-w-full object-contain opacity-95 group-hover:opacity-100 transition-opacity duration-500"
                    />
                  </div>
                )}

                <h3 className="relative font-display text-base sm:text-lg lg:text-2xl uppercase tracking-tight leading-tight group-hover:text-fd-gold-bright transition-colors duration-500">
                  {g.label}
                </h3>
              </Link>
              </Tilt>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
  );
};
